import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import Yup from '../../../utils/yupMethods';
import UserService from '../../../services/UserService/UserService';
import SuccessMessage from '../../../components/SuccessMessage/SuccessMessage';
import Button from '../../../components/Button/Button';
import UserContext from '../../../context/UserContext/UserContext';
import getPasswordStrengthPlusOne from '../../../utils/getPasswordStrengthPlusOne';
import PasswordStrengthMeter from '../../../components/PasswordStrengthMeter/PasswordStrengthMeter';
import CustomInput from '../../../components/CustomInput/CustomInput';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

function UpdatePasswordForm(): React.ReactElement {
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [passwordScore, setPasswordScore] = React.useState<number>(0);

  const { user, setUser } = React.useContext(UserContext);

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const pw = ev.target.value;
    if (pw.length < 1) {
      setPasswordScore(0);
      return;
    }
    const passwordStrength = getPasswordStrengthPlusOne(pw);
    setPasswordScore(passwordStrength);
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().when([], {
      is: () => user?.hasPassword === true,
      then: Yup.string().required('Password is required'),
      otherwise: Yup.string()
    }),
    newPassword: Yup.string()
      .required('New password is required')
      .passwordStrength()
      .notOneOf(
        [Yup.ref('currentPassword')],
        'New password must be different to your current password'
      ),
    confirmNewPassword: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    }
  });

  const onSubmit = (data: FormValues): void => {
    setIsLoading(true);
    UserService.updatePassword(data.currentPassword, data.newPassword)
      .then((res) => {
        setErrorMessage('');
        setSuccessMessage(res);
        setPasswordScore(0);
        reset();
        if (user) {
          setUser({
            ...user,
            hasPassword: true
          });
        }
      })
      .catch((err) => {
        setSuccessMessage('');
        if (err.name === 'CanceledError') return;
        const error =
          err.apiError?.message ||
          'Something went wrong. Please try again later.';
        setErrorMessage(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input
        name='username'
        id='username'
        value={user?.email}
        autoComplete='username'
        hidden
        readOnly
      />
      <fieldset className='border-0 p-0 mb-2'>
        <div className='w-full'>
          {user?.hasPassword && (
            <Controller
              name='currentPassword'
              control={control}
              render={({ field }): React.ReactElement => (
                <CustomInput
                  {...field}
                  label='Current password'
                  type='password'
                  autoComplete='current-password'
                  error={errors.currentPassword}
                />
              )}
            />
          )}
          <Controller
            name='newPassword'
            control={control}
            render={({ field }): React.ReactElement => (
              <CustomInput
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e);
                }}
                label='New password'
                type='password'
                autoComplete='new-password'
                error={errors.newPassword}
              />
            )}
          />
          <PasswordStrengthMeter score={passwordScore} className='mb-4' />
          <Controller
            name='confirmNewPassword'
            control={control}
            render={({ field }): React.ReactElement => (
              <CustomInput
                {...field}
                label='Confirm new password'
                type='password'
                autoComplete='new-password'
                containerClassName='mt-4'
                error={errors.confirmNewPassword}
              />
            )}
          />
        </div>
      </fieldset>
      <ErrorMessage message={errorMessage} />
      <SuccessMessage message={successMessage} />
      <Button type='submit' loading={isLoading} className='mt-3'>
        Update password
      </Button>
    </form>
  );
}

export default UpdatePasswordForm;
